import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { hapticLight, hapticSelection } from '@/utils/haptic';
import { toast } from 'sonner';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tracks, setTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [trackDuration, setTrackDuration] = useState(0); // Track duration in seconds
  const [trackEnded, setTrackEnded] = useState(false); // Track if current track has ended
  const audioRef = useRef(null);
  const failedUrlsRef = useRef(new Map()); // Cache failed URLs with timestamps for auto-expiry
  const progressMilestoneCallbacksRef = useRef([]); // Callbacks for progress milestones
  const onTrackEndCallbackRef = useRef(null); // Callback for when track ends
  
  const FAILED_URL_EXPIRY_MS = 30000; // 30 seconds - allow retry after temporary failures
  
  // Check if a URL is in the failed cache and not expired
  const isUrlFailed = useCallback((url) => {
    if (!failedUrlsRef.current.has(url)) return false;
    const failedTime = failedUrlsRef.current.get(url);
    if (Date.now() - failedTime > FAILED_URL_EXPIRY_MS) {
      // Expired, remove from cache
      failedUrlsRef.current.delete(url);
      failedUrlsRef.current.delete(url + '_toasted');
      return false;
    }
    return true;
  }, []);
  
  // Mark a URL as failed with timestamp
  const markUrlFailed = useCallback((url) => {
    failedUrlsRef.current.set(url, Date.now());
  }, []);

  // Normalize audio URLs specifically
  const normalizeAudioUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') {
      console.warn('[PlayerContext] normalizeAudioUrl: Invalid URL', url);
      return '';
    }
    let normalized = url.trim();
    if (!normalized) {
      console.warn('[PlayerContext] normalizeAudioUrl: Empty URL after trim');
      return '';
    }
    
    // If URL is already fully qualified and correct, return as-is
    if (normalized.startsWith('https://soundope.com/api/uploads/')) {
      return normalized;
    }
    
    // Handle relative paths (old format)
    if (normalized.startsWith('/uploads/') || normalized.startsWith('uploads/')) {
      normalized = `https://soundope.com/api/uploads/${normalized.replace(/^\/?uploads\//, '')}`;
    } else if (normalized.startsWith('/api/uploads/')) {
      normalized = `https://soundope.com${normalized}`;
    } else if (normalized.startsWith('/') && !normalized.startsWith('//')) {
      // Other relative paths
      normalized = `https://soundope.com${normalized}`;
    } else if (normalized.startsWith('http://')) {
      normalized = normalized.replace(/^http:\/\//i, 'https://');
    } else if (!normalized.startsWith('https://')) {
      // Fallback: assume uploads path
      normalized = `https://soundope.com/api/uploads/${normalized}`;
    }
    
    // Convert old /uploads/ URLs to /api/uploads/ - but avoid double /api/api/
    if (normalized.includes('soundope.com/uploads/') && !normalized.includes('/api/uploads/')) {
      normalized = normalized.replace('soundope.com/uploads/', 'soundope.com/api/uploads/');
    }
    
    return normalized;
  }, []);

  // Normalize image URLs - less aggressive, preserves external URLs
  const normalizeImageUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') {
      return '';
    }
    let normalized = url.trim();
    if (!normalized) {
      return '';
    }
    
    // If it's already a fully qualified https URL, return as-is
    if (normalized.startsWith('https://')) {
      // Only convert soundope.com/uploads/ to soundope.com/api/uploads/
      if (normalized.includes('soundope.com/uploads/') && !normalized.includes('/api/uploads/')) {
        normalized = normalized.replace('soundope.com/uploads/', 'soundope.com/api/uploads/');
      }
      return normalized;
    }
    
    // Convert http to https
    if (normalized.startsWith('http://')) {
      return normalized.replace(/^http:\/\//i, 'https://');
    }
    
    // Handle relative paths for soundope uploads
    if (normalized.startsWith('/uploads/') || normalized.startsWith('uploads/')) {
      return `https://soundope.com/api/uploads/${normalized.replace(/^\/?uploads\//, '')}`;
    } else if (normalized.startsWith('/api/uploads/')) {
      return `https://soundope.com${normalized}`;
    }
    
    // For other relative paths, return as-is (may be local assets)
    return normalized;
  }, []);

  const hasPlayableUrl = useCallback((track) => {
    if (!track?.audio_url) return false;
    const url = track.audio_url.trim();
    if (!url) return false;
    // Check if it's a valid audio file URL - support Supabase storage, soundope uploads, and any https audio
    return /^https:\/\/.+\.(mp3|m4a|wav|ogg|flac)(\?.*)?$/i.test(url) || 
           url.includes('supabase.co/storage/') ||
           url.includes('/uploads/');
  }, []);

  const playTrack = useCallback(
    (track, trackList = null, index = 0, enableAutoPlay = false, startPlaying = true) => {
      if (!track) {
        setCurrentTrack(null);
        setIsPlaying(false);
        return;
      }

      const normalizedUrl = normalizeAudioUrl(track.audio_url);
      
      // Check if this URL has already failed before setting it as current track
      if (normalizedUrl && isUrlFailed(normalizedUrl)) {
        console.warn('[PlayerContext] Attempted to play track with failed URL:', normalizedUrl);
        toast.error('This track\'s audio file is missing and cannot be played.');
        return;
      }

      const normalizedTrack = {
        ...track,
        audio_url: normalizedUrl,
        cover_image_url: normalizeImageUrl(track.cover_image_url),
      };

      const normalizedList = trackList
        ? trackList.map((t) => ({
            ...t,
            audio_url: normalizeAudioUrl(t.audio_url),
            cover_image_url: normalizeImageUrl(t.cover_image_url),
          }))
        : null;

      setCurrentTrack(normalizedTrack);
      if (normalizedList) {
        setTracks(normalizedList);
        setCurrentTrackIndex(index);
      }
      setAutoPlayEnabled(enableAutoPlay);
      // Auto-play the new track immediately when clicking on it
      setIsPlaying(startPlaying);
      setProgress(0);
      setTrackDuration(0); // Reset duration when track changes
    },
    [normalizeAudioUrl, normalizeImageUrl]
  );

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (!hasPlayableUrl(currentTrack)) {
      setIsPlaying(false);
      toast.error('Cannot play this track. Audio file is missing or invalid.');
      return;
    }
    hapticSelection();
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Let the effect handle play after src is (re)loaded
      setIsPlaying(true);
    }
  };

  const skipToNext = useCallback(() => {
    if (tracks.length > 0) {
      hapticLight();
      const nextIndex = (currentTrackIndex + 1) % tracks.length;
      setCurrentTrackIndex(nextIndex);
      setCurrentTrack(tracks[nextIndex]);
      setProgress(0);
      setIsPlaying(true);
    }
  }, [tracks, currentTrackIndex]);

  const skipToPrevious = useCallback(() => {
    if (tracks.length > 0) {
      hapticLight();
      const prevIndex = currentTrackIndex === 0 ? tracks.length - 1 : currentTrackIndex - 1;
      setCurrentTrackIndex(prevIndex);
      setCurrentTrack(tracks[prevIndex]);
      setProgress(0);
      setIsPlaying(true);
    }
  }, [tracks, currentTrackIndex]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      if (!duration || !Number.isFinite(duration) || duration <= 0) {
        return;
      }
      
      // Update track duration if it changed
      if (duration !== trackDuration) {
        setTrackDuration(Math.round(duration));
      }
      
      const progressPercent = (currentTime / duration) * 100;
      setProgress(progressPercent);
      
      // Call milestone callbacks (e.g., for every 10% progress)
      const milestone = Math.floor(progressPercent / 10) * 10;
      progressMilestoneCallbacksRef.current.forEach(callback => {
        if (typeof callback === 'function') {
          callback(progressPercent, milestone, currentTime, duration);
        }
      });
    }
  }, [trackDuration]);

  const handleTrackEnd = useCallback(() => {
    setTrackEnded(true);
    setIsPlaying(false);
    setProgress(100);
    
    // Call the registered callback if exists
    if (onTrackEndCallbackRef.current) {
      onTrackEndCallbackRef.current();
    } else if (autoPlayEnabled && tracks.length > 0) {
      // Fallback to auto-skip if no callback registered
      skipToNext();
    }
  }, [autoPlayEnabled, tracks.length, skipToNext]);

  // Function to register a callback for when track ends
  const registerOnTrackEnd = useCallback((callback) => {
    onTrackEndCallbackRef.current = callback;
    return () => {
      onTrackEndCallbackRef.current = null;
    };
  }, []);

  // Reset trackEnded when track changes
  useEffect(() => {
    setTrackEnded(false);
  }, [currentTrack?.id]);

  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    if (!currentTrack) {
      setIsPlaying(false);
      return;
    }

    if (!hasPlayableUrl(currentTrack)) {
      console.warn('[PlayerContext] Track has no playable URL:', currentTrack);
      setIsPlaying(false);
      if (currentTrack.audio_url) {
        toast.error('Cannot play this track. Audio file URL is invalid or missing.');
      }
      return;
    }

    const normalizedUrl = normalizeAudioUrl(currentTrack.audio_url);
    if (!normalizedUrl) {
      console.error('[PlayerContext] Failed to normalize URL:', currentTrack.audio_url);
      setIsPlaying(false);
      toast.error('Cannot play this track. Audio file URL is invalid.');
      return;
    }

    // Check if this URL has already failed
    if (isUrlFailed(normalizedUrl)) {
      console.warn('[PlayerContext] Skipping previously failed URL:', normalizedUrl);
      setIsPlaying(false);
      toast.error('This track\'s audio file is missing and cannot be played.');
      return;
    }

    // Set src directly to ensure the latest URL is used
    audio.pause();
    audio.currentTime = 0;
    audio.src = normalizedUrl;
    audio.load();

    if (isPlaying) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          console.error('[PlayerContext] Play error:', err, 'URL:', normalizedUrl);
          // Mark this URL as failed with timestamp
          markUrlFailed(normalizedUrl);
          setIsPlaying(false);
          // Only show error toast once per URL
          if (!failedUrlsRef.current.has(normalizedUrl + '_toasted')) {
            failedUrlsRef.current.set(normalizedUrl + '_toasted', Date.now());
            toast.error('Audio file not found. This track may have been deleted or moved.');
          }
        });
      }
    }
  }, [currentTrack, isPlaying, hasPlayableUrl, normalizeAudioUrl, isUrlFailed, markUrlFailed]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleAudioError = (e) => {
      console.error('Audio element error, stopping playback', e);
      setIsPlaying(false);
      
      // Mark the current URL as failed if we have a current track
      if (currentTrack?.audio_url) {
        const normalizedUrl = normalizeAudioUrl(currentTrack.audio_url);
        if (normalizedUrl) {
          markUrlFailed(normalizedUrl);
          // Only show error once per URL
          if (!failedUrlsRef.current.has(normalizedUrl + '_toasted')) {
            failedUrlsRef.current.set(normalizedUrl + '_toasted', Date.now());
            toast.error('Audio file not found. This track may have been deleted or moved.');
          }
        }
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleTrackEnd);
    audio.addEventListener('error', handleAudioError);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleTrackEnd);
      audio.removeEventListener('error', handleAudioError);
    };
  }, [handleTimeUpdate, handleTrackEnd, currentTrack, normalizeAudioUrl, markUrlFailed]);

  // Function to register progress milestone callbacks
  const registerProgressCallback = useCallback((callback) => {
    if (typeof callback === 'function') {
      progressMilestoneCallbacksRef.current.push(callback);
      // Return cleanup function
      return () => {
        const index = progressMilestoneCallbacksRef.current.indexOf(callback);
        if (index > -1) {
          progressMilestoneCallbacksRef.current.splice(index, 1);
        }
      };
    }
    return () => {}; // No-op cleanup
  }, []);

  // Function to clear failed URLs cache (useful after new uploads)
  const clearFailedUrls = useCallback(() => {
    failedUrlsRef.current.clear();
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        progress,
        tracks,
        currentTrackIndex,
        audioRef,
        playTrack,
        togglePlayPause,
        skipToNext,
        skipToPrevious,
        setIsPlaying,
        setProgress,
        setTracks,
        setCurrentTrackIndex,
        setCurrentTrack,
        autoPlayEnabled,
        setAutoPlayEnabled,
        trackDuration,
        registerProgressCallback,
        clearFailedUrls,
        trackEnded,
        setTrackEnded,
        registerOnTrackEnd,
      }}
    >
      <audio ref={audioRef} className="hidden" />
      {children}
    </PlayerContext.Provider>
  );
};

PlayerProvider.propTypes = {
  children: PropTypes.node,
};
